import { useEffect, useRef, type ReactElement } from "react";
import { useReducedMotion } from "@src/packages/moon-ui/hooks/useReducedMotion";
import {
  Animated,
  Easing,
  View,
  Image,
  Platform,
  StyleSheet,
} from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import Svg, { Path, Line } from "react-native-svg";

const grainTexture =
  require("@src/apps/moon/assets/images/gradient-grain-texture-2.jpg") as number;

type Tone = "cream" | "amber" | "copper" | "brown" | "frost";

const TONES: Record<Tone, [string, string, string]> = {
  cream: ["#FFF8EC", "#F1D1A6", "#B9784C"],
  amber: ["#FFE8A5", "#F4B34F", "#9C5528"],
  copper: ["#F2B387", "#B76545", "#542F27"],
  brown: ["#9C604D", "#5B332C", "#2F1D19"],
  frost: ["#FFFDFC", "#F3E5CF", "#B88C68"],
};

const MUTED_MAP: Record<Tone, Tone> = {
  cream: "frost",
  amber: "cream",
  copper: "cream",
  brown: "frost",
  frost: "frost",
};

const CANVAS_W = 180;
const CANVAS_H = 150;

// ─── Primitives ────────────────────────────────────────────────────────────

function Blob({
  x,
  y,
  w,
  h,
  tone,
  rotate = 0,
  opacity = 1,
  motion,
  index = 0,
  highlight = true,
}: {
  x: number;
  y: number;
  w: number;
  h: number;
  tone: Tone;
  rotate?: number;
  opacity?: number;
  motion: Animated.Value;
  index?: number;
  highlight?: boolean;
}) {
  const translateY = motion.interpolate({
    inputRange: [0, 1],
    outputRange: [index % 2 === 0 ? -2 : 2, index % 2 === 0 ? 3 : -3],
  });
  const scale = motion.interpolate({
    inputRange: [0, 1],
    outputRange: [0.99, 1.03],
  });
  const r = motion.interpolate({
    inputRange: [0, 1],
    outputRange: [`${rotate - 1.5}deg`, `${rotate + 1.5}deg`],
  });
  return (
    <Animated.View
      style={{
        position: "absolute",
        left: x,
        top: y,
        width: w,
        height: h,
        opacity,
        borderRadius: Math.max(w, h),
        overflow: "hidden",
        transform: [{ translateY }, { scale }, { rotate: r }],
      }}
    >
      <LinearGradient
        colors={TONES[tone]}
        start={{ x: 0.15, y: 0.08 }}
        end={{ x: 0.85, y: 0.94 }}
        style={{ flex: 1, borderRadius: Math.max(w, h) }}
      />
      <Image
        source={grainTexture}
        resizeMode="cover"
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          right: 0,
          bottom: 0,
          opacity: 0.16,
        }}
      />
      {highlight && (
        <View
          pointerEvents="none"
          style={{
            position: "absolute",
            left: w * 0.22,
            top: h * 0.16,
            width: w * 0.26,
            height: h * 0.22,
            borderRadius: Math.max(w, h),
            backgroundColor: "rgba(255,255,255,0.46)",
            opacity: 0.7,
          }}
        />
      )}
    </Animated.View>
  );
}

function Dot({
  x,
  y,
  size,
  tone,
  opacity = 1,
  motion,
  index = 0,
}: {
  x: number;
  y: number;
  size: number;
  tone: Tone;
  opacity?: number;
  motion: Animated.Value;
  index?: number;
}) {
  const translateY = motion.interpolate({
    inputRange: [0, 1],
    outputRange: [index % 2 === 0 ? 1 : -1, index % 2 === 0 ? -2 : 2],
  });
  return (
    <Animated.View
      style={{
        position: "absolute",
        left: x,
        top: y,
        width: size,
        height: size,
        opacity,
        transform: [{ translateY }],
      }}
    >
      <LinearGradient
        colors={TONES[tone]}
        start={{ x: 0.2, y: 0.1 }}
        end={{ x: 0.8, y: 1 }}
        style={{
          flex: 1,
          borderRadius: 999,
          borderWidth: 1,
          borderColor: "rgba(255,255,255,0.34)",
        }}
      />
    </Animated.View>
  );
}

function Ring({
  x,
  y,
  size,
  color = "rgba(255,255,255,0.55)",
  thickness = 1,
  dashed = false,
}: {
  x: number;
  y: number;
  size: number;
  color?: string;
  thickness?: number;
  dashed?: boolean;
}) {
  return (
    <View
      pointerEvents="none"
      style={{
        position: "absolute",
        left: x,
        top: y,
        width: size,
        height: size,
        borderRadius: size / 2,
        borderWidth: thickness,
        borderColor: color,
        borderStyle: dashed ? "dashed" : "solid",
      }}
    />
  );
}

function Sparkle({
  x,
  y,
  size = 8,
  color = "rgba(255,255,255,0.75)",
}: {
  x: number;
  y: number;
  size?: number;
  color?: string;
}) {
  return (
    <View
      pointerEvents="none"
      style={{
        position: "absolute",
        left: x,
        top: y,
        width: size,
        height: size,
      }}
    >
      <View
        style={{
          position: "absolute",
          top: size / 2 - 0.5,
          left: 0,
          width: size,
          height: 1,
          backgroundColor: color,
        }}
      />
      <View
        style={{
          position: "absolute",
          left: size / 2 - 0.5,
          top: 0,
          width: 1,
          height: size,
          backgroundColor: color,
        }}
      />
    </View>
  );
}

// ─── Per-cycle compositions ────────────────────────────────────────────────

function EggFreezingScene({
  motion,
  toneFor,
}: {
  motion: Animated.Value;
  toneFor: (t: Tone) => Tone;
}) {
  // A single glowing egg, encircled by a frost ring, with sparkles around it.
  return (
    <>
      <Ring x={20} y={28} size={104} color="rgba(255,255,255,0.32)" />
      <Ring x={8} y={16} size={128} color="rgba(255,255,255,0.18)" dashed />
      <Blob
        x={36}
        y={44}
        w={72}
        h={72}
        tone={toneFor("cream")}
        rotate={-4}
        motion={motion}
        index={0}
      />
      <Blob
        x={56}
        y={62}
        w={40}
        h={40}
        tone={toneFor("amber")}
        opacity={0.78}
        rotate={6}
        motion={motion}
        index={1}
        highlight={false}
      />
      <Sparkle x={130} y={28} />
      <Sparkle x={146} y={66} size={6} />
      <Sparkle x={120} y={110} size={6} color="rgba(255,255,255,0.55)" />
      <Sparkle x={28} y={120} size={5} color="rgba(255,255,255,0.45)" />
    </>
  );
}

function OiScene({
  motion,
  toneFor,
}: {
  motion: Animated.Value;
  toneFor: (t: Tone) => Tone;
}) {
  // Ovulation induction: a cohort of three maturing follicles over a warm glow.
  return (
    <>
      {/* Warm radial-ish glow */}
      <Blob
        x={16}
        y={26}
        w={140}
        h={108}
        tone={toneFor("amber")}
        opacity={0.22}
        rotate={3}
        motion={motion}
        index={0}
        highlight={false}
      />
      {/* Primary follicle */}
      <Blob
        x={30}
        y={50}
        w={62}
        h={62}
        tone={toneFor("cream")}
        rotate={-4}
        motion={motion}
        index={1}
      />
      <Blob
        x={48}
        y={68}
        w={26}
        h={26}
        tone={toneFor("amber")}
        opacity={0.55}
        rotate={5}
        motion={motion}
        index={2}
        highlight={false}
      />
      {/* Secondary follicle — smaller, upper-right */}
      <Blob
        x={94}
        y={22}
        w={42}
        h={42}
        tone={toneFor("cream")}
        opacity={0.92}
        rotate={3}
        motion={motion}
        index={3}
      />
      <Blob
        x={104}
        y={32}
        w={20}
        h={20}
        tone={toneFor("amber")}
        opacity={0.45}
        rotate={3}
        motion={motion}
        index={4}
        highlight={false}
      />
      {/* Tertiary follicle — lower-right */}
      <Blob
        x={100}
        y={82}
        w={50}
        h={50}
        tone={toneFor("cream")}
        opacity={0.95}
        rotate={-2}
        motion={motion}
        index={5}
      />
      <Blob
        x={114}
        y={96}
        w={22}
        h={22}
        tone={toneFor("amber")}
        opacity={0.5}
        rotate={-3}
        motion={motion}
        index={6}
        highlight={false}
      />
      <Sparkle x={26} y={124} size={5} color="rgba(255,255,255,0.4)" />
      <Sparkle x={148} y={50} size={6} color="rgba(255,255,255,0.45)" />
    </>
  );
}

function EmbryoFreezingScene({
  motion,
  toneFor,
}: {
  motion: Animated.Value;
  toneFor: (t: Tone) => Tone;
}) {
  // Blastocyst: outer membrane ring + cluster of blastomeres + one inner cell mass.
  return (
    <>
      <Ring x={18} y={14} size={130} color="rgba(255,255,255,0.30)" />
      {/* Outer cell cluster */}
      <Dot
        x={36}
        y={56}
        size={26}
        tone={toneFor("cream")}
        motion={motion}
        index={0}
      />
      <Dot
        x={58}
        y={36}
        size={28}
        tone={toneFor("cream")}
        motion={motion}
        index={1}
      />
      <Dot
        x={88}
        y={32}
        size={26}
        tone={toneFor("frost")}
        motion={motion}
        index={2}
      />
      <Dot
        x={116}
        y={48}
        size={30}
        tone={toneFor("cream")}
        motion={motion}
        index={3}
      />
      <Dot
        x={120}
        y={86}
        size={26}
        tone={toneFor("frost")}
        motion={motion}
        index={4}
      />
      <Dot
        x={96}
        y={102}
        size={28}
        tone={toneFor("cream")}
        motion={motion}
        index={5}
      />
      <Dot
        x={62}
        y={102}
        size={24}
        tone={toneFor("cream")}
        motion={motion}
        index={6}
      />
      <Dot
        x={38}
        y={86}
        size={26}
        tone={toneFor("frost")}
        motion={motion}
        index={7}
      />
      {/* Inner cell mass — distinct, warmer */}
      <Dot
        x={70}
        y={62}
        size={38}
        tone={toneFor("amber")}
        motion={motion}
        index={8}
      />
    </>
  );
}

function SpermFreezingScene({
  motion,
  toneFor,
}: {
  motion: Animated.Value;
  toneFor: (t: Tone) => Tone;
}) {
  // Curved sperm: heads + tails drawn as paths, with frost dots in background.
  return (
    <>
      {/* Background frost dots */}
      <Dot
        x={140}
        y={18}
        size={6}
        tone={toneFor("frost")}
        opacity={0.5}
        motion={motion}
        index={0}
      />
      <Dot
        x={26}
        y={120}
        size={5}
        tone={toneFor("frost")}
        opacity={0.5}
        motion={motion}
        index={1}
      />
      <Dot
        x={150}
        y={108}
        size={4}
        tone={toneFor("frost")}
        opacity={0.5}
        motion={motion}
        index={2}
      />
      {/* Tails */}
      <View pointerEvents="none" style={StyleSheet.absoluteFill}>
        <Svg width={CANVAS_W} height={CANVAS_H}>
          <Path
            d="M28 56 Q 64 80, 92 56 T 154 60"
            stroke="rgba(243,229,207,0.85)"
            strokeWidth={2}
            fill="none"
            strokeLinecap="round"
          />
          <Path
            d="M20 96 Q 56 76, 88 96 T 150 92"
            stroke="rgba(243,229,207,0.65)"
            strokeWidth={2}
            fill="none"
            strokeLinecap="round"
          />
          <Path
            d="M32 124 Q 64 108, 96 124 T 150 124"
            stroke="rgba(243,229,207,0.45)"
            strokeWidth={1.5}
            fill="none"
            strokeLinecap="round"
          />
        </Svg>
      </View>
      {/* Heads — small ellipses at the tail ends */}
      <Blob
        x={140}
        y={52}
        w={22}
        h={16}
        tone={toneFor("amber")}
        rotate={-8}
        motion={motion}
        index={0}
      />
      <Blob
        x={140}
        y={84}
        w={20}
        h={14}
        tone={toneFor("cream")}
        rotate={4}
        motion={motion}
        index={1}
      />
      <Blob
        x={140}
        y={116}
        w={18}
        h={12}
        tone={toneFor("copper")}
        rotate={-6}
        motion={motion}
        index={2}
        opacity={0.85}
      />
    </>
  );
}

function IvfScene({
  motion,
  toneFor,
}: {
  motion: Animated.Value;
  toneFor: (t: Tone) => Tone;
}) {
  // Petri-dish ring + egg + a sperm streak approaching with a halo highlight.
  return (
    <>
      <Ring x={14} y={20} size={130} color="rgba(255,255,255,0.22)" />
      <Ring x={20} y={26} size={118} color="rgba(255,255,255,0.32)" />
      {/* Egg */}
      <Blob
        x={28}
        y={50}
        w={72}
        h={72}
        tone={toneFor("cream")}
        rotate={-4}
        motion={motion}
        index={0}
      />
      <Blob
        x={48}
        y={68}
        w={36}
        h={36}
        tone={toneFor("amber")}
        opacity={0.8}
        rotate={5}
        motion={motion}
        index={1}
        highlight={false}
      />
      {/* Sperm approaching */}
      <View pointerEvents="none" style={StyleSheet.absoluteFill}>
        <Svg width={CANVAS_W} height={CANVAS_H}>
          <Path
            d="M156 36 Q 132 60, 110 78"
            stroke="rgba(183,101,69,0.75)"
            strokeWidth={2}
            fill="none"
            strokeLinecap="round"
          />
          <Line
            x1={150}
            y1={40}
            x2={158}
            y2={32}
            stroke="rgba(255,255,255,0.6)"
            strokeWidth={1}
          />
        </Svg>
      </View>
      <Blob
        x={140}
        y={28}
        w={20}
        h={14}
        tone={toneFor("copper")}
        rotate={-12}
        motion={motion}
        index={2}
      />
      {/* Contact halo */}
      <Ring x={92} y={68} size={36} color="rgba(255,232,165,0.45)" />
    </>
  );
}

function IuiScene({
  motion,
  toneFor,
}: {
  motion: Animated.Value;
  toneFor: (t: Tone) => Tone;
}) {
  // Uterus-like vessel (rounded pear shape via overlapping blobs) + catheter streak entering.
  return (
    <>
      {/* Vessel — two stacked blobs to suggest a pear/uterus shape */}
      <Blob
        x={48}
        y={48}
        w={72}
        h={84}
        tone={toneFor("cream")}
        rotate={4}
        motion={motion}
        index={0}
      />
      <Blob
        x={66}
        y={36}
        w={36}
        h={28}
        tone={toneFor("cream")}
        rotate={-8}
        motion={motion}
        index={1}
        opacity={0.85}
        highlight={false}
      />
      {/* Inside markers */}
      <Dot
        x={72}
        y={84}
        size={10}
        tone={toneFor("amber")}
        motion={motion}
        index={2}
      />
      <Dot
        x={92}
        y={96}
        size={8}
        tone={toneFor("amber")}
        motion={motion}
        index={3}
        opacity={0.8}
      />
      <Dot
        x={80}
        y={106}
        size={6}
        tone={toneFor("copper")}
        motion={motion}
        index={4}
        opacity={0.7}
      />
      {/* Catheter line from top-right */}
      <View pointerEvents="none" style={StyleSheet.absoluteFill}>
        <Svg width={CANVAS_W} height={CANVAS_H}>
          <Path
            d="M150 10 Q 124 22, 100 38"
            stroke="rgba(183,101,69,0.7)"
            strokeWidth={2}
            fill="none"
            strokeLinecap="round"
          />
        </Svg>
      </View>
      <Dot
        x={148}
        y={6}
        size={8}
        tone={toneFor("copper")}
        motion={motion}
        index={5}
      />
    </>
  );
}

const SCENES: Record<
  string,
  (args: { motion: Animated.Value; toneFor: (t: Tone) => Tone }) => ReactElement
> = {
  egg_freezing: EggFreezingScene,
  embryo_freezing: EmbryoFreezingScene,
  sperm_freezing: SpermFreezingScene,
  ivf: IvfScene,
  ivf_donor_egg: IvfScene,
  ivf_donor_sperm: IvfScene,
  iui: IuiScene,
  oi: OiScene,
};

type Props = {
  cycleType: string | null | undefined;
  muted?: boolean;
};

export function CycleIllustration({ cycleType, muted = false }: Props) {
  const Scene = (cycleType ? SCENES[cycleType] : undefined) ?? EggFreezingScene;
  const motion = useRef(new Animated.Value(0)).current;
  const reducedMotion = useReducedMotion();

  useEffect(() => {
    if (muted || reducedMotion) return;
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(motion, {
          toValue: 1,
          duration: 3400,
          easing: Easing.inOut(Easing.sin),
          useNativeDriver: Platform.OS !== "web",
        }),
        Animated.timing(motion, {
          toValue: 0,
          duration: 3400,
          easing: Easing.inOut(Easing.sin),
          useNativeDriver: Platform.OS !== "web",
        }),
      ]),
    );
    loop.start();
    return () => loop.stop();
  }, [motion, muted, reducedMotion]);

  const toneFor = (t: Tone): Tone => (muted ? MUTED_MAP[t] : t);

  return (
    <View
      pointerEvents="none"
      style={{
        width: CANVAS_W,
        height: CANVAS_H,
        opacity: muted ? 0.75 : 1,
      }}
    >
      <Scene motion={motion} toneFor={toneFor} />
    </View>
  );
}
